import { DealRole, Side } from "@/enum/common.enum";
import { resolveRole, roleLabel } from "@/utils/deals.utils";

type AnyRecord = Record<string, any>;

/**
 * Ownership resolution for a checklist row, for the surfaces an *agent* reads.
 *
 * The product principle the owner set is that a task can be owned by a role
 * before it is owned by a person: `seeds.py` stamps every seeded row with an
 * `assigned_role` and never a user, and deal setup is not allowed to demand an
 * assignment. That is already how the data works. What was missing is the other
 * half — resolving that role to whoever currently fills it, and saying plainly
 * when nobody does.
 *
 * There is no `assigned_user_id` anywhere in the product, so nothing here reads
 * one. Ownership is derived entirely from the deal's own people, which is why
 * inviting a lender silently gives every lender row an owner with no rework.
 *
 * Kept separate from `resolveTaskOwner` in `deals-view.utils.ts`, which answers
 * the same question in the client's voice ("your lender") off a deliberately
 * narrowed team list. Merging them would force one wording on both audiences.
 */

/** Whether a person actually occupies the seat, and how firmly. */
export type OwnershipState = "named" | "pending" | "unassigned";

export interface DealPerson {
  role: string;
  name: string | null;
  side: string | null;
  /** "active" once they accept; "pending"/"expired" while an invite is out. */
  status: string;
}

export interface TaskOwnership {
  /** The stored `assigned_role`, verbatim. */
  role: string;
  /** Side-aware role key — `buyer_agent`, `seller_client`, `closing_attorney`. */
  resolvedRole: string;
  /** That key as a human label: "Buyer's agent". Never blank. */
  roleLabel: string;
  /** The person filling the seat, when there is one. */
  name: string | null;
  /** Name when we have one, otherwise the role. What a cell should print. */
  label: string;
  state: OwnershipState;
  /** Why the seat is not settled, for the agent. Null once it is. */
  hint: string | null;
}

/**
 * The deal's people, normalized across the two payload shapes that carry them.
 *
 * `internal_full` sends `participants` (accepted rows merged with pending
 * invitations, so each carries a `status`); `client_calm` sends `team`, which
 * `_client_team` has already filtered to accepted participants — hence the
 * `active` default rather than a guess.
 */
export function dealPeople(deal: AnyRecord | null | undefined): DealPerson[] {
  const rows = deal?.participants || deal?.team || [];
  return (Array.isArray(rows) ? rows : []).map((row: AnyRecord) => ({
    role: String(row?.role || ""),
    name: row?.name ?? null,
    side: row?.side ?? null,
    status: String(row?.status || "active"),
  }));
}

const clean = (value: unknown): string | null => {
  const text = String(value ?? "").trim();
  return text || null;
};

/** A side-less participant is on the deal for everyone — legacy rows and
 *  neutral vendors both land here, and excluding them would strand real people. */
const onSide = (person: DealPerson, side: string): boolean =>
  !person.side || person.side === side || person.side === Side.NEUTRAL;

function findPerson(people: DealPerson[], role: string, side: string): DealPerson | null {
  const matches = people.filter((person) => person.role === role && onSide(person, side));
  // An accepted person outranks an outstanding invite for the same seat.
  return matches.find((person) => person.status === "active") || matches[0] || null;
}

export function resolveTaskOwnership(
  task: AnyRecord | null | undefined,
  deal: AnyRecord | null | undefined,
): TaskOwnership {
  const role = String(task?.assigned_role || "");
  const hostSide = deal?.deal_type === Side.BUY ? Side.BUY : Side.SELL;
  const side = task?.side === Side.BUY || task?.side === Side.SELL ? task.side : hostSide;
  const isHostSide = side === hostSide;
  const people = dealPeople(deal);

  const resolvedRole = resolveRole({ role, side, hostDealType: deal?.deal_type }) || role;
  // `roleLabel` degrades an unknown key to spaced words, so this is never blank
  // for a role we have; the final fallback covers a row with no role at all.
  const label = roleLabel(resolvedRole) || "Unassigned";

  let person: DealPerson | null = null;
  if (role === DealRole.AGENT || role === DealRole.CO_AGENT) {
    // The host agent owns their own side. The other side's agent rows belong to
    // whichever co-agent represents it — never to the host, who would otherwise
    // appear to owe the counterparty's work.
    person = isHostSide
      ? { role, name: clean(deal?.agent_name) || clean(deal?.agent?.name), side, status: "active" }
      : findPerson(people, DealRole.CO_AGENT, side);
  } else if (role === DealRole.CLIENT) {
    person = isHostSide
      ? { role, name: clean(deal?.client_name), side, status: "active" }
      : findPerson(people, DealRole.CLIENT, side);
  } else if (role) {
    person = findPerson(people, role, side);
  }

  const name = clean(person?.name);
  const pending = Boolean(person) && person!.status !== "active";
  const state: OwnershipState = !name ? "unassigned" : pending ? "pending" : "named";

  return {
    role,
    resolvedRole,
    roleLabel: label,
    name,
    label: name || label,
    state,
    hint: state === "named" ? null
      : state === "pending" ? "Invite pending"
      : `Waiting for ${label.toLowerCase()} to be added`,
  };
}
